// SETTINGS V2 — desktop rail (SETTINGS_V2_SP0 §7.2).
//
// 248px card: user header (real customerAccount data) + 3 groups of NavLink
// items. Port of designer SUserHead + SNavItem + Shell B grouping
// (settings-shell.jsx lines 129-156, 178-204), as a real component.
//
// Inline styles + CSS-var tokens only (resolve inside .settings-v2-root); no
// MUI / sx / dt / Tailwind. Hover is expressed via the `.sv2-nav-item` class in
// the token sheet (inline styles can't express :hover); the active state is
// applied inline and wins on specificity over the class hover.
//
// SP1 seams (do not build here): SettingsV2Layout will branch on useBreakpoint()
// — desktop renders this rail, tablet SettingsV2TabBar.tsx, phone
// SettingsV2SectionSelector.tsx + bottom sheet driven by
// settingsV2Ui.phoneNavOpen. SP0 renders the rail unconditionally.
import React from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import useSettingsV2Nav, {
  SettingsV2NavItem,
  isSettingsV2SectionActive,
} from "../useSettingsV2Nav";
import { SETTINGS_BASE } from "../settingsV2Paths";
import settingsV2NavGlyphs from "./settingsV2NavGlyphs";

const SettingsV2Rail: React.FC = () => {
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const { groups } = useSettingsV2Nav();
  const userInfo = useAppSelector((s) => s.customerAccount.userInfo);

  // Exact-or-nested match now lives in the shared isSettingsV2SectionActive
  // helper (§4.2) — same behavior, one source of truth across all nav surfaces.

  // Port the LIVE initials logic verbatim (SettingsSidebar.tsx lines 45-48):
  // '?' ONLY when userInfo is null; empty string when present-but-empty.
  const initials = userInfo
    ? `${(userInfo.firstName || "").charAt(0)}${(userInfo.lastName || "").charAt(0)}`.toUpperCase()
    : "?";

  const renderItem = (item: SettingsV2NavItem) => {
    const active = isSettingsV2SectionActive(location.pathname, item.path);
    const Glyph = settingsV2NavGlyphs[item.key];
    return (
      <NavLink
        key={item.key}
        to={`${SETTINGS_BASE}/${item.path}`}
        style={{ textDecoration: "none" }}
      >
        <div
          className="sv2-nav-item"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            height: 38,
            padding: "0 12px",
            borderRadius: 8,
            minWidth: 0,
            fontSize: 13,
            // Inactive rows deliberately DON'T set inline background/color — the
            // token sheet's `.sv2-nav-item` base + `:hover` own those. Active
            // rows set them inline (wins over the class hover on specificity).
            borderLeft: active
              ? "2px solid var(--mod-accent)"
              : "2px solid transparent",
            fontWeight: active ? 600 : 500,
            ...(active
              ? {
                  background: "var(--mod-accent-soft)",
                  color: "var(--mod-accent)",
                }
              : {}),
          }}
        >
          {Glyph && <Glyph />}
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {translate(item.labelKey)}
          </span>
        </div>
      </NavLink>
    );
  };

  return (
    <aside
      style={{
        width: 248,
        flexShrink: 0,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        overflow: "hidden",
        animation: "sv2-fade-up 0.4s ease-out both",
      }}
    >
      {/* User header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 14px 12px",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 999,
            background: "var(--mod-accent)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : ""}
          </div>
          {/* Email is desktop-only by construction in SP0 (the whole rail is
              desktop-only). SP1's phone selector must NOT show the email. */}
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {userInfo?.email || ""}
          </div>
        </div>
      </div>

      {/* Groups */}
      <div style={{ padding: "6px 8px 12px" }}>
        {groups.map((group) => (
          <div key={group.key}>
            <div
              className="eyebrow"
              style={{ color: "var(--faint)", margin: "12px 6px 5px" }}
            >
              {translate(group.labelKey)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {group.items.map(renderItem)}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default SettingsV2Rail;
